import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  QrCode,
  Star,
  Zap,
  TrendingUp,
  Eye,
  Building2,
  ExternalLink,
  Utensils,
  ShoppingBag,
  Hotel,
  Stethoscope,
  Store,
  Wrench,
  Shield,
  BarChart3,
  Bell,
  ThumbsUp
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TAPBACK_URL = 'https://www.tapback.in';
const TAPBACK_DEMO_URL = 'https://www.tapback.in/register';

// Royal Pista Green Palette
const PAGE_BG = '#4f6b53';
const PAGE_BG_DEEP = '#3d5a40';
const PAGE_BG_LIGHT = '#5d7d62';
const GOLD = '#d4af37';
const GOLD_LIGHT = '#e8c963';
const CREAM = '#f5ebd6';
const CREAM_SECONDARY = 'rgba(245, 235, 214, 0.85)';
const CREAM_MUTED = 'rgba(245, 235, 214, 0.65)';
const GOLD_SOFT = 'rgba(212, 175, 55, 0.12)';
const GOLD_BORDER = 'rgba(212, 175, 55, 0.4)';
const CARD_BG = 'rgba(255, 255, 255, 0.05)';
const CARD_BORDER = 'rgba(245, 235, 214, 0.18)';

// Subtle paper-grain SVG texture, applied as background overlay for a "royal textured" feel
const TEXTURE_SVG = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.85   0 0 0 0 0.78   0 0 0 0 0.55   0 0 0 0.22 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.45'/></svg>")`;

const TapbackDetail = () => {
  const benefits = [
    { icon: <ThumbsUp size={32} />, title: 'Stronger Customer Trust', description: 'Show guests their voice matters with visible responsiveness and a transparent feedback wall.' },
    { icon: <Zap size={32} />, title: 'Faster Complaint Resolution', description: 'Critical feedback reaches the right person instantly — recover service before guests leave.' },
    { icon: <Eye size={32} />, title: 'Real-Time Sentiment Visibility', description: 'Owners and managers see what customers feel — outlet by outlet, hour by hour.' },
    { icon: <TrendingUp size={32} />, title: 'Clearer Decision-Making', description: 'Simple, outcome-focused reports help leaders fix what matters and double down on what works.' },
    { icon: <Star size={32} />, title: 'More Positive Visibility', description: 'Genuine guest praise becomes a public asset — building reputation across every location.' },
    { icon: <Building2 size={32} />, title: 'Consistent Multi-Outlet Experience', description: 'One platform, every outlet — track and elevate quality across single stores or large chains.' }
  ];

  const steps = [
    { number: '01', icon: <QrCode size={40} />, title: 'Customer Scans', description: 'A simple QR code at the table, counter, or packaging opens the feedback experience instantly — no app, no login, no typing.' },
    { number: '02', icon: <Star size={40} />, title: 'Customer Rates', description: 'Friendly star ratings and quick taps capture how the guest truly felt — in seconds, on any phone.' },
    { number: '03', icon: <Bell size={40} />, title: 'Business Acts', description: 'You see what guests are saying in real time — and critical feedback is escalated to the right person, immediately.' }
  ];

  const useCases = [
    { icon: <Utensils size={28} />, title: 'Food & Beverage', description: 'Cafés, QSRs, dine-in restaurants, cloud kitchens' },
    { icon: <ShoppingBag size={28} />, title: 'Retail Stores', description: 'Flagship stores, fashion, electronics, lifestyle' },
    { icon: <Hotel size={28} />, title: 'Hotels & Hospitality', description: 'Boutique stays, resorts, banquets, lounges' },
    { icon: <Stethoscope size={28} />, title: 'Healthcare', description: 'Clinics, diagnostics, dental, wellness centers' },
    { icon: <Store size={28} />, title: 'Malls & Common Areas', description: 'Food courts, washrooms, parking, entry zones' },
    { icon: <Wrench size={28} />, title: 'Service Businesses', description: 'Salons, gyms, auto-care, fitness studios' }
  ];

  const trustPillars = [
    { icon: <Eye size={28} />, title: 'Live Feedback Wall', description: 'A public-facing display of genuine guest voices that signals confidence, openness, and continuous improvement.' },
    { icon: <Bell size={28} />, title: 'Instant Escalation', description: 'When a critical concern lands, the right manager is notified at once — turning complaints into recovery moments.' },
    { icon: <Shield size={28} />, title: 'Genuine Ratings', description: 'Dashboards reflect honest feedback, so the score on your wall is a score you can stand behind.' },
    { icon: <BarChart3 size={28} />, title: 'Outlet-Wise Reporting', description: 'Simple, ready-to-read reports by location, staff, and category — clarity for owners, action for managers.' }
  ];

  // Shared section styles
  const sectionBase = {
    position: 'relative',
    color: CREAM,
  };

  return (
    <div data-testid="tapback-detail-page" style={{
      background: PAGE_BG,
      minHeight: '100vh',
      color: CREAM
    }}>
      <Header />
      <main style={{ paddingTop: '180px', position: 'relative' }}>

        {/* Global texture overlay across the whole page */}
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundImage: TEXTURE_SVG,
          backgroundRepeat: 'repeat',
          opacity: 0.55,
          pointerEvents: 'none',
          zIndex: 0,
          mixBlendMode: 'overlay'
        }} />

        {/* HERO */}
        <section style={{
          ...sectionBase,
          background: `
            radial-gradient(circle at 18% 22%, rgba(212, 175, 55, 0.18) 0%, transparent 45%),
            radial-gradient(circle at 82% 78%, rgba(232, 201, 99, 0.10) 0%, transparent 50%),
            linear-gradient(160deg, ${PAGE_BG_LIGHT} 0%, ${PAGE_BG} 50%, ${PAGE_BG_DEEP} 100%)
          `,
          padding: '60px 7.6923% 100px',
          overflow: 'hidden'
        }}>
          {/* Decorative QR-inspired grid pattern */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(212, 175, 55, 0.06) 1px, transparent 1px),
              linear-gradient(90deg, rgba(212, 175, 55, 0.06) 1px, transparent 1px)
            `,
            backgroundSize: '42px 42px',
            opacity: 0.6,
            pointerEvents: 'none'
          }} />

          {/* Ornamental corner flourishes */}
          <div aria-hidden style={{
            position: 'absolute',
            top: 40,
            right: 40,
            width: 120,
            height: 120,
            borderTop: `1px solid ${GOLD_BORDER}`,
            borderRight: `1px solid ${GOLD_BORDER}`,
            opacity: 0.8
          }} />
          <div aria-hidden style={{
            position: 'absolute',
            bottom: 40,
            left: 40,
            width: 120,
            height: 120,
            borderBottom: `1px solid ${GOLD_BORDER}`,
            borderLeft: `1px solid ${GOLD_BORDER}`,
            opacity: 0.8
          }} />

          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <Link to="/" data-testid="tapback-back-link" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: GOLD_LIGHT,
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '40px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to Solutions
            </Link>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.1fr 0.9fr',
              gap: '80px',
              alignItems: 'center'
            }} className="tb-hero-grid">
              {/* Left */}
              <div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 16px',
                  background: GOLD_SOFT,
                  border: `1px solid ${GOLD_BORDER}`,
                  marginBottom: '32px',
                  fontSize: '13px',
                  color: GOLD_LIGHT,
                  fontWeight: '600',
                  letterSpacing: '2px',
                  textTransform: 'uppercase'
                }}>
                  <Zap size={14} />
                  A VDM Business Innovations Initiative
                </div>

                <h2 style={{
                  fontSize: '40px',
                  fontWeight: '800',
                  color: GOLD,
                  letterSpacing: '-1.5px',
                  marginBottom: '24px',
                  lineHeight: 1
                }}>
                  tap<span style={{ color: CREAM }}>back</span>
                </h2>

                <h1 style={{
                  marginBottom: '24px',
                  fontSize: 'clamp(40px, 5vw, 64px)',
                  fontWeight: '700',
                  lineHeight: 1.05,
                  letterSpacing: '-1.5px',
                  color: CREAM
                }}>
                  Turn every QR scan into <span style={{ color: GOLD }}>insight, recovery, and revenue.</span>
                </h1>
                <p style={{
                  marginBottom: '40px',
                  color: CREAM_SECONDARY,
                  maxWidth: '620px',
                  lineHeight: 1.7,
                  fontSize: '18px'
                }}>
                  A fast, frictionless feedback platform that helps businesses capture guest feedback in seconds, respond sooner, and improve service in real time.
                </p>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '40px' }}>
                  <a
                    href={TAPBACK_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tapback-hero-demo-cta"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '14px 28px',
                      background: GOLD,
                      color: PAGE_BG_DEEP,
                      fontWeight: '700',
                      fontSize: '15px',
                      letterSpacing: '0.5px',
                      textDecoration: 'none',
                      border: `1px solid ${GOLD}`,
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = GOLD_LIGHT;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 28px rgba(212, 175, 55, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = GOLD;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Request a Demo
                    <ArrowRight size={18} />
                  </a>
                  <a
                    href="#how-it-works"
                    data-testid="tapback-hero-how-cta"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '14px 28px',
                      background: 'transparent',
                      color: CREAM,
                      fontWeight: '600',
                      fontSize: '15px',
                      letterSpacing: '0.5px',
                      textDecoration: 'none',
                      border: `1px solid ${CARD_BORDER}`,
                      transition: 'all 0.3s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = GOLD;
                      e.currentTarget.style.color = GOLD_LIGHT;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = CARD_BORDER;
                      e.currentTarget.style.color = CREAM;
                    }}
                  >
                    See How It Works
                  </a>
                </div>

                {/* Quick benefit chips */}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  {[
                    { icon: <QrCode size={16} />, label: 'No app · No login' },
                    { icon: <Zap size={16} />, label: 'Feedback in seconds' },
                    { icon: <Bell size={16} />, label: 'Instant escalation' }
                  ].map((chip, i) => (
                    <div key={i} style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: CREAM_MUTED,
                      fontSize: '14px'
                    }}>
                      <span style={{ color: GOLD_LIGHT }}>{chip.icon}</span>
                      {chip.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - QR Visual */}
              <div style={{ position: 'relative' }} className="tb-hero-visual">
                <div style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '1 / 1',
                  maxWidth: '460px',
                  margin: '0 auto'
                }}>
                  {/* Floating accent box */}
                  <div style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    padding: '18px 22px',
                    background: GOLD,
                    color: PAGE_BG_DEEP,
                    fontWeight: '700',
                    fontSize: '14px',
                    letterSpacing: '0.5px',
                    boxShadow: '0 14px 40px rgba(212,175,55,0.35)',
                    zIndex: 3
                  }}>
                    SCAN. RATE. DONE.
                  </div>

                  {/* QR card */}
                  <div style={{
                    background: CREAM,
                    padding: '36px',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    border: `2px solid ${GOLD}`,
                    boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
                    position: 'relative',
                    zIndex: 2
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#7a7160', letterSpacing: '2px' }}>OUTLET</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: PAGE_BG_DEEP, marginTop: '4px' }}>Brew &amp; Bite</div>
                        <div style={{ fontSize: '12px', color: '#8a8270', marginTop: '2px' }}>Koramangala · Bangalore</div>
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: '800', color: GOLD }}>
                        tap<span style={{ color: PAGE_BG_DEEP }}>back</span>
                      </div>
                    </div>

                    {/* SVG QR-like pattern */}
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '20px 0'
                    }}>
                      <svg viewBox="0 0 100 100" style={{ width: '70%', height: 'auto' }} aria-label="QR code illustration">
                        <rect x="5" y="5" width="22" height="22" fill={PAGE_BG_DEEP} />
                        <rect x="9" y="9" width="14" height="14" fill={CREAM} />
                        <rect x="13" y="13" width="6" height="6" fill={PAGE_BG_DEEP} />

                        <rect x="73" y="5" width="22" height="22" fill={PAGE_BG_DEEP} />
                        <rect x="77" y="9" width="14" height="14" fill={CREAM} />
                        <rect x="81" y="13" width="6" height="6" fill={PAGE_BG_DEEP} />

                        <rect x="5" y="73" width="22" height="22" fill={PAGE_BG_DEEP} />
                        <rect x="9" y="77" width="14" height="14" fill={CREAM} />
                        <rect x="13" y="81" width="6" height="6" fill={PAGE_BG_DEEP} />

                        {[...Array(20)].map((_, i) => {
                          const x = 32 + (i % 8) * 4.5;
                          const y = 32 + Math.floor(i / 8) * 4.5;
                          return <rect key={`a-${i}`} x={x} y={y} width="3.5" height="3.5" fill={PAGE_BG_DEEP} opacity={i % 2 === 0 ? 1 : 0.85} />;
                        })}
                        {[...Array(24)].map((_, i) => {
                          const x = 32 + (i % 12) * 4.5;
                          const y = 60 + Math.floor(i / 12) * 4.5;
                          return <rect key={`b-${i}`} x={x} y={y} width="3.5" height="3.5" fill={PAGE_BG_DEEP} opacity={i % 3 === 0 ? 1 : 0.7} />;
                        })}
                        {[...Array(12)].map((_, i) => {
                          const x = 73 + (i % 4) * 5;
                          const y = 35 + Math.floor(i / 4) * 5;
                          return <rect key={`c-${i}`} x={x} y={y} width="4" height="4" fill={PAGE_BG_DEEP} opacity={i % 2 === 0 ? 1 : 0.6} />;
                        })}
                        <circle cx="50" cy="50" r="7" fill={CREAM} />
                        <circle cx="50" cy="50" r="6" fill={GOLD} />
                      </svg>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingTop: '16px',
                      borderTop: `1px dashed ${PAGE_BG_LIGHT}`
                    }}>
                      <div style={{ display: 'flex', gap: '6px', fontSize: '20px' }}>
                        <span>😡</span><span>🙁</span><span>😐</span><span>🙂</span><span>😍</span>
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: '#7a7160', letterSpacing: '1.5px' }}>
                        TAP TO RATE
                      </div>
                    </div>
                  </div>

                  {/* Floating mini badge */}
                  <div style={{
                    position: 'absolute',
                    bottom: '-25px',
                    left: '-25px',
                    background: PAGE_BG_DEEP,
                    border: `2px solid ${GOLD}`,
                    padding: '14px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    zIndex: 3,
                    boxShadow: '0 14px 40px rgba(0,0,0,0.35)'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontSize: '11px', color: CREAM_MUTED, letterSpacing: '1.5px', fontWeight: '600' }}>AVG RATING</span>
                      <span style={{ fontSize: '22px', color: GOLD_LIGHT, fontWeight: '800' }}>4.6 ★</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM & SOLUTION */}
        <section style={{ ...sectionBase, padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '40px'
            }} className="tb-problem-grid">
              <div style={{
                padding: '48px',
                background: 'rgba(180, 90, 70, 0.12)',
                border: '1px solid rgba(220, 150, 120, 0.35)',
                backdropFilter: 'blur(2px)'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#e8b89a', letterSpacing: '2px', marginBottom: '16px' }}>
                  THE PROBLEM
                </div>
                <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '20px', color: CREAM, lineHeight: 1.2 }}>
                  Most feedback never reaches you.
                </h3>
                <p style={{ fontSize: '16px', color: CREAM_SECONDARY, lineHeight: 1.8 }}>
                  Long forms, app downloads, and slow surveys cause guests to walk away silently — taking complaints, suggestions, and goodwill with them. By the time managers find out, the moment is already gone.
                </p>
              </div>

              <div style={{
                padding: '48px',
                background: GOLD_SOFT,
                border: `1px solid ${GOLD_BORDER}`,
                backdropFilter: 'blur(2px)'
              }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: GOLD_LIGHT, letterSpacing: '2px', marginBottom: '16px' }}>
                  THE SOLUTION
                </div>
                <h3 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '20px', color: CREAM, lineHeight: 1.2 }}>
                  Feedback that&apos;s instant, simple, and mobile-first.
                </h3>
                <p style={{ fontSize: '16px', color: CREAM_SECONDARY, lineHeight: 1.8 }}>
                  TAPBACK puts a frictionless QR code wherever your guests are. A scan, a tap, and you&apos;ve captured what matters — before the moment passes, and before the customer walks out the door.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section id="how-it-works" style={{ ...sectionBase, padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center', marginBottom: '70px' }}>
              <div style={{
                display: 'inline-block',
                padding: '6px 14px',
                background: GOLD_SOFT,
                border: `1px solid ${GOLD_BORDER}`,
                marginBottom: '20px',
                fontSize: '12px',
                color: GOLD_LIGHT,
                fontWeight: '600',
                letterSpacing: '2px'
              }}>
                HOW IT WORKS
              </div>
              <h2 style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: '700', marginBottom: '20px', color: CREAM, letterSpacing: '-1px' }}>
                Three taps from <span style={{ color: GOLD }}>customer to action</span>.
              </h2>
              <p style={{ fontSize: '17px', maxWidth: '720px', margin: '0 auto', color: CREAM_SECONDARY, lineHeight: 1.7 }}>
                A flow so simple your guests don&apos;t notice they&apos;re giving feedback — and so smart your team can act on it before they&apos;ve left.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
              gap: '30px'
            }}>
              {steps.map((step, i) => (
                <div key={i} style={{
                  padding: '44px 32px',
                  background: CARD_BG,
                  border: `1px solid ${CARD_BORDER}`,
                  position: 'relative',
                  transition: 'all 0.4s ease-in-out',
                  backdropFilter: 'blur(2px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = GOLD;
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.background = GOLD_SOFT;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = CARD_BORDER;
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = CARD_BG;
                }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '24px',
                    fontSize: '64px',
                    fontWeight: '800',
                    color: GOLD,
                    opacity: 0.18,
                    lineHeight: 1
                  }}>
                    {step.number}
                  </div>
                  <div style={{ color: GOLD_LIGHT, marginBottom: '24px' }}>
                    {step.icon}
                  </div>
                  <h3 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '14px', color: CREAM }}>
                    {step.title}
                  </h3>
                  <p style={{ fontSize: '14px', color: CREAM_SECONDARY, lineHeight: 1.7 }}>
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section style={{ ...sectionBase, padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '6px 14px',
                background: GOLD_SOFT,
                border: `1px solid ${GOLD_BORDER}`,
                marginBottom: '20px',
                fontSize: '12px',
                color: GOLD_LIGHT,
                fontWeight: '600',
                letterSpacing: '2px'
              }}>
                THE OUTCOMES
              </div>
              <h2 style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: '700', marginBottom: '20px', color: CREAM, letterSpacing: '-1px' }}>
                Better feedback. <span style={{ color: GOLD }}>Better business.</span>
              </h2>
              <p style={{ fontSize: '17px', maxWidth: '720px', margin: '0 auto', color: CREAM_SECONDARY, lineHeight: 1.7 }}>
                What happens when your guests can speak in seconds — and your team can respond in minutes.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {benefits.map((b, i) => (
                <div key={i} style={{
                  padding: '36px 32px',
                  background: CARD_BG,
                  border: `1px solid ${CARD_BORDER}`,
                  transition: 'all 0.4s ease-in-out',
                  backdropFilter: 'blur(2px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = GOLD;
                  e.currentTarget.style.background = GOLD_SOFT;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = CARD_BORDER;
                  e.currentTarget.style.background = CARD_BG;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ color: GOLD_LIGHT, marginBottom: '20px' }}>{b.icon}</div>
                  <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px', color: CREAM }}>{b.title}</h3>
                  <p style={{ fontSize: '14px', color: CREAM_SECONDARY, lineHeight: 1.7 }}>
                    {b.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* USE CASES */}
        <section style={{ ...sectionBase, padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <div style={{
                display: 'inline-block',
                padding: '6px 14px',
                background: GOLD_SOFT,
                border: `1px solid ${GOLD_BORDER}`,
                marginBottom: '20px',
                fontSize: '12px',
                color: GOLD_LIGHT,
                fontWeight: '600',
                letterSpacing: '2px'
              }}>
                BUILT FOR EVERY GUEST-FACING BUSINESS
              </div>
              <h2 style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: '700', marginBottom: '20px', color: CREAM, letterSpacing: '-1px' }}>
                One platform. <span style={{ color: GOLD }}>Every industry.</span>
              </h2>
              <p style={{ fontSize: '17px', maxWidth: '720px', margin: '0 auto', color: CREAM_SECONDARY, lineHeight: 1.7 }}>
                Wherever a customer meets your brand, TAPBACK turns that moment into measurable improvement.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '20px'
            }}>
              {useCases.map((u, i) => (
                <div key={i} style={{
                  padding: '32px',
                  background: CARD_BG,
                  border: `1px solid ${CARD_BORDER}`,
                  transition: 'all 0.4s ease-in-out',
                  display: 'flex',
                  gap: '18px',
                  alignItems: 'flex-start',
                  backdropFilter: 'blur(2px)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = GOLD;
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = CARD_BORDER;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{
                    width: '52px',
                    height: '52px',
                    background: GOLD_SOFT,
                    border: `1px solid ${GOLD_BORDER}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: GOLD_LIGHT,
                    flexShrink: 0
                  }}>
                    {u.icon}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '6px', color: CREAM }}>
                      {u.title}
                    </h3>
                    <p style={{ fontSize: '13px', color: CREAM_MUTED, lineHeight: 1.6 }}>
                      {u.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TRUST & ACTION */}
        <section style={{ ...sectionBase, padding: '100px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '60px',
              alignItems: 'center'
            }} className="tb-trust-grid">
              <div>
                <div style={{
                  display: 'inline-block',
                  padding: '6px 14px',
                  background: GOLD_SOFT,
                  border: `1px solid ${GOLD_BORDER}`,
                  marginBottom: '20px',
                  fontSize: '12px',
                  color: GOLD_LIGHT,
                  fontWeight: '600',
                  letterSpacing: '2px'
                }}>
                  TRUST &amp; ACTION
                </div>
                <h2 style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: '700', marginBottom: '24px', color: CREAM, letterSpacing: '-1px', lineHeight: 1.15 }}>
                  A feedback wall that <span style={{ color: GOLD }}>customers trust</span> — and a system that <span style={{ color: GOLD }}>moves on it</span>.
                </h2>
                <p style={{ fontSize: '16px', color: CREAM_SECONDARY, marginBottom: '36px', lineHeight: 1.8 }}>
                  TAPBACK gives your business two powerful signals at once: a live, public-facing feedback wall that shows responsiveness, and instant escalation that turns critical moments into recovery wins. The result is a brand that listens — visibly, consistently, everywhere.
                </p>

                <div style={{ display: 'grid', gap: '16px' }}>
                  {trustPillars.map((p, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      gap: '18px',
                      padding: '20px 22px',
                      background: CARD_BG,
                      border: `1px solid ${CARD_BORDER}`,
                      backdropFilter: 'blur(2px)'
                    }}>
                      <div style={{ color: GOLD_LIGHT, flexShrink: 0 }}>{p.icon}</div>
                      <div>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: CREAM }}>
                          {p.title}
                        </h4>
                        <p style={{ fontSize: '13px', color: CREAM_MUTED, lineHeight: 1.6 }}>
                          {p.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Mock feedback wall */}
              <div style={{
                padding: '32px',
                background: `linear-gradient(135deg, ${GOLD_SOFT} 0%, rgba(212, 175, 55, 0.04) 100%)`,
                border: `1px solid ${GOLD_BORDER}`,
                position: 'relative',
                backdropFilter: 'blur(3px)'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '24px',
                  paddingBottom: '16px',
                  borderBottom: `1px solid ${GOLD_BORDER}`
                }}>
                  <div>
                    <div style={{ fontSize: '11px', letterSpacing: '2px', color: GOLD_LIGHT, fontWeight: '600' }}>LIVE FEEDBACK WALL</div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: CREAM, marginTop: '4px' }}>Today · All Outlets</div>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    color: GOLD_LIGHT,
                    fontWeight: '600'
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: GOLD_LIGHT,
                      animation: 'pulse 2s infinite'
                    }} />
                    LIVE
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '14px' }}>
                  {[
                    { rating: 5, name: 'Priya S.', text: 'Loved the new menu — staff remembered my coffee order!', outlet: 'Indiranagar' },
                    { rating: 5, name: 'Arjun M.', text: 'Quick service, clean tables, exactly what I needed.', outlet: 'Koramangala' },
                    { rating: 4, name: 'Neha R.', text: 'Great vibe, music could be a touch lower.', outlet: 'HSR Layout' },
                    { rating: 5, name: 'Karan V.', text: 'Resolved my complaint within minutes. Impressed!', outlet: 'Whitefield' }
                  ].map((f, i) => (
                    <div key={i} style={{
                      padding: '16px 18px',
                      background: 'rgba(0,0,0,0.18)',
                      border: `1px solid ${CARD_BORDER}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          {[...Array(5)].map((_, s) => (
                            <Star key={s} size={14} fill={s < f.rating ? GOLD : 'transparent'} color={s < f.rating ? GOLD : 'rgba(245,235,214,0.3)'} />
                          ))}
                        </div>
                        <span style={{ fontSize: '11px', color: CREAM_MUTED, letterSpacing: '1px' }}>
                          {f.outlet.toUpperCase()}
                        </span>
                      </div>
                      <p style={{ fontSize: '13px', color: CREAM_SECONDARY, lineHeight: 1.6, marginBottom: '6px' }}>
                        &ldquo;{f.text}&rdquo;
                      </p>
                      <div style={{ fontSize: '11px', color: CREAM_MUTED }}>— {f.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section style={{ ...sectionBase, padding: '100px 7.6923% 100px' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
            <div style={{
              padding: '80px 60px',
              background: `linear-gradient(135deg, rgba(212, 175, 55, 0.18) 0%, rgba(212, 175, 55, 0.05) 100%)`,
              border: `2px solid ${GOLD}`,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              backdropFilter: 'blur(3px)'
            }}>
              {/* Decorative ornament */}
              <div aria-hidden style={{
                position: 'absolute',
                top: '-40px',
                right: '-40px',
                width: '200px',
                height: '200px',
                opacity: 0.12,
                backgroundImage: `
                  linear-gradient(${GOLD} 2px, transparent 2px),
                  linear-gradient(90deg, ${GOLD} 2px, transparent 2px)
                `,
                backgroundSize: '20px 20px'
              }} />

              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                padding: '6px 14px',
                background: 'rgba(0,0,0,0.22)',
                border: `1px solid ${GOLD_BORDER}`,
                marginBottom: '24px',
                fontSize: '12px',
                color: GOLD_LIGHT,
                fontWeight: '600',
                letterSpacing: '2px'
              }}>
                <QrCode size={14} /> READY IN MINUTES
              </div>
              <h2 style={{ fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: '700', marginBottom: '20px', color: CREAM, letterSpacing: '-1px' }}>
                Start capturing better feedback today with <span style={{ color: GOLD }}>TAPBACK</span>.
              </h2>
              <p style={{ fontSize: '18px', marginBottom: '40px', maxWidth: '720px', margin: '0 auto 40px', color: CREAM_SECONDARY, lineHeight: 1.7 }}>
                Generate your first QR code, drop it on a table or counter, and watch the insights — and the recovery moments — roll in.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '24px' }}>
                <a
                  href={TAPBACK_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tapback-cta-demo"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '14px 28px',
                    background: GOLD,
                    color: PAGE_BG_DEEP,
                    fontWeight: '700',
                    fontSize: '15px',
                    letterSpacing: '0.5px',
                    textDecoration: 'none',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = GOLD_LIGHT;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = GOLD;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Request a Demo
                  <ArrowRight size={18} />
                </a>
                <a
                  href={TAPBACK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="tapback-cta-visit"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '14px 28px',
                    background: 'transparent',
                    color: CREAM,
                    fontWeight: '600',
                    fontSize: '15px',
                    letterSpacing: '0.5px',
                    textDecoration: 'none',
                    border: `1px solid ${CREAM}`,
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = GOLD_LIGHT;
                    e.currentTarget.style.color = GOLD_LIGHT;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = CREAM;
                    e.currentTarget.style.color = CREAM;
                  }}
                >
                  Visit www.tapback.in
                  <ExternalLink size={18} />
                </a>
              </div>
              <Link
                to="/#contact"
                data-testid="tapback-cta-contact"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '14px',
                  color: CREAM_MUTED,
                  textDecoration: 'none'
                }}
              >
                Or contact the VDM team <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
        @media (max-width: 900px) {
          .tb-hero-grid,
          .tb-problem-grid,
          .tb-trust-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .tb-hero-visual {
            max-width: 420px;
            margin: 0 auto;
          }
        }
      `}</style>
    </div>
  );
};

export default TapbackDetail;
